import _ from 'lodash'

import { log } from '@/log'
import Parameter, { ParameterList } from '@/models/api/payment/parameter/parameter'
import UserSelect, { SelectOptionList } from '@/models/api/payment/parameter/type/user-select'
import { PaymentType } from '@/models/app-config/behavior/types'

/**
 * @see https://my-color.esa.io/posts/534
 */
export default class OptionsValidator {

  constructor(
    private parameterList: ParameterList<Parameter>,
    private selectedOptions: SelectOptionList
  ) {}

  get userSelectParameters(): ParameterList<UserSelect> {
    return this.parameterList.onlyUserSelect
  }

  validate(): ErrorBag {
    return this.validateRequiredParameters(this.userSelectParameters, this.selectedOptions)
  }

  private validateRequiredParameters(
    userSelectParameters: ParameterList<UserSelect>,
    selectedOptions: SelectOptionList
  ): ErrorBag {
    const requiredParameterIds = userSelectParameters.filter(p => p.required).map(p => p.id)
    const selectedOptionsParameterIds = selectedOptions.map(o => o.parameterId)
    const notSelectedRequiredParameterIds = _.difference(requiredParameterIds, selectedOptionsParameterIds)

    if (notSelectedRequiredParameterIds.length > 0) {
      log.warn(`Parameter ${notSelectedRequiredParameterIds} are required.`)
    }

    /**
     * TODO: 各支払い方法の下に、そのパラメータがあるという構造で、ここではパラメータのみをIDとしてエラー結果に詰めている。
     * そのため、各エラーがどの支払い方法のものかという情報が存在していない。
     * できれば各エラーがどの支払い方法のものか識別できたほうが、パラメータとしては同名だが違う支払い方法に属するものが出てきたときに厄介である。
     * しかし少なくとも既存の仕組みが、支払い方法に属するかを無視してただパラメータを並列に並べて、必須なのに未選択なパラメータの有無だけを判定しているので、
     * その状況から後退したわけではない。したがって、エラーに支払い方法への参照も含めることはTODOとする。
     * client側で型調整してもよいが、server側で各パラメータにそれがどの支払い方法に属しているかを予め詰めておいたほうがよいかもしれない。
     */
    const errorBag = new ErrorBag(notSelectedRequiredParameterIds.map((id) => {
      return {
        paymentParameterId: id,
        type: 'required',
      }
    }))

    return errorBag
  }
}

export class ErrorBag {
  static empty(): ErrorBag {
    return new ErrorBag([])
  }

  constructor(private readonly errors: ValidationError[]) {}

  find(paymentParameterId: string, type: ValidationErrorType): ValidationError | null {
    return this.errors.find(error => error.paymentParameterId === paymentParameterId && error.type === type) || null
  }

  get hasSomeError(): boolean {
    return this.errors.length > 0
  }

  /**
   * 引数で指定された支払い方法が選択されているとき、その選択に関連するバリデーション結果にもとづき、適切なエラーメッセージを返す。
   */
  validationMessageFor(paymentType: PaymentType): string {
    switch (paymentType) {
      case 'cvs-number':
        if (!!this.find('cvs_type', 'required')) {
          return 'コンビニを選択してください'
        }

        return ''
      case 'carrier':
        if (!!this.find('carrier_provider', 'required')) {
          return 'キャリアを選択してください'
        }

        return ''
      default:
        return ''
    }
  }
}

export interface ValidationError {
  paymentParameterId: string
  type: ValidationErrorType
}

type ValidationErrorType = 'required'
